'use client';

import { Typography } from '@mui/material';
import { type ComponentType, createContext, type ElementType, useContext } from 'react';
import type { StructuredContent } from '@repo/supergraph/structuredContent/types';
import { Accordion } from './components/Accordion';
import { AccordionItem } from './components/AccordionItem';
import { AnchorLink } from './components/AnchorLink';
import { AnchorLinkTarget } from './components/AnchorLinkTarget';
import { BackToTopLink } from './components/BackToTopLink';
import { BainGenericContainer } from './components/BainGenericContainer';
import { BankingAttentionNote } from './components/BankingAttentionNote';
import { BankingCalculator } from './components/BankingCalculator';
import { BankingWhatTheNerdsThink } from './components/BankingWhatTheNerdsThink';
import { Button } from './components/Button';
import { Citation } from './components/Citation';
import type { ICitation } from './components/Citation/context';
import { Column } from './components/Columns/Column';
import { Columns } from './components/Columns/Columns';
import { Container } from './components/Container';
import { ContentHeading } from './components/ContentHeading';
import { ContentImage } from './components/ContentImage';
import { ContentRibbon } from './components/ContentRibbon';
import { ContentSection } from './components/ContentSection';
import { Divider } from './components/Divider';
import { DocumentStructuredContent } from './components/Document';
import CalculatedFieldsForm from './components/Embeds/CalculatedFieldsForm';
import Infogram from './components/Embeds/Infogram';
import { ExpandCard } from './components/ExpandCard';
import { FAQ } from './components/FAQ';
import { FirstPersonPerspective } from './components/FirstPersonPerspective';
import { FirstPersonPerspectiveItem } from './components/FirstPersonPerspectiveItem';
import { IconCardList } from './components/IconCardList';
import { Li } from './components/Li';
import { Link } from './components/Link';
import { List } from './components/List';
import { MarketplaceField } from './components/MarketplaceField';
import { MarketplaceRateTable } from './components/MarketplaceRateTable';
import { MiniProductCard } from './components/MiniProductCard';
import { NerdTip } from './components/NerdTip';
import { NerdyTip } from './components/NerdyTip';
import { NWVerifiedContainer } from './components/NWVerifiedContainer';
import { SMBIllustrationCardList } from './components/SMBIllustrationCardList';
import { SMBInfoGatheringCta } from './components/SMBInfoGatheringCta';
import { SMBProsAndConsTable } from './components/SMBProsAndConsTable';
import {
  SMBWhyTrustNerdWallet,
  SMBWhyTrustNerdWalletBullet,
} from './components/SMBWhyTrustNerdWallet';
import { Spacer } from './components/Spacer';
import { StarRating } from './components/StarRating';
import { StarRatingCategories } from './components/StarRatingCategories';
import { Superscript } from './components/Superscript';
import { TableOfContents } from './components/TableOfContents';
import { populateTableOfContents } from './components/TableOfContents/utils';
import { Type } from './components/Type';
import { Well } from './components/Well';
import { WPBlock } from './components/WPBlock';
import { WPDynamicPlacement } from './components/WPDynamicPlacement';
import { WPHouseAd } from './components/WPHouseAd';
import { WPTable } from './components/WPTable';
import { WPVideo } from './components/WPVideo';
import { NodeContextProvider } from './NodeContext';
import {
  groupedByH2ContentAndWrapItWithContainer,
  injectRatingIntoStarRatingCategories,
} from './utils';

export type StructuredContentRendererProps = {
  BankingCalculator?: {
    calculator?: string;
    calculatorRegistry?: Record<string, ComponentType>;
  };
  FAQ?: {
    canonicalUrl?: string;
    defaultExpanded?: boolean;
  };
  TableOfContents?: {
    isCalculatorPage?: boolean;
  };
};

const StructuredContentRendererPropsContext = createContext<
  StructuredContentRendererProps | undefined
>(undefined);

const components: Record<string, ElementType> = {
  Accordion,
  AccordionItem,
  AnchorLink,
  AnchorLinkTarget,
  BackToTopLink,
  BainGenericContainer,
  Button,
  CalculatedFieldsForm,
  Citation,
  Columns,
  Column,
  Container,
  ContentHeading,
  ContentImage,
  ContentSection,
  ContentRibbon,
  Divider,
  Document: DocumentStructuredContent,
  ExpandCard,
  FAQ,
  // Experiment,
  FirstPersonPerspective,
  FirstPersonPerspectiveItem,
  IconCardList,
  Infogram,
  Li,
  Link,
  List,
  MarketplaceField,
  MarketplaceRateTable,
  MiniProductCard,
  NerdTip,
  NWVerifiedContainer,
  SMBIllustrationCardList,
  NerdyTip,
  SMBInfoGatheringCta,
  SMBProsAndConsTable,
  SMBWhyTrustNerdWallet,
  SMBWhyTrustNerdWalletBullet,
  Spacer,
  Superscript,
  StarRating,
  StarRatingCategories,
  TableOfContents,
  Type,
  Well,
  BankingWhatTheNerdsThink,
  BankingAttentionNote,
  BankingCalculator,
  WPBlock,
  WPDynamicPlacement,
  WPHouseAd,
  Video: WPVideo,
  WPTable,
};

export const StructuredContentRenderer = ({
  doc,
  tableOfContentsEnabled = false,
  overallRating,
  excludeHeadings,
  rendererProps,
}: {
  doc: StructuredContent;
  tableOfContentsEnabled?: boolean;
  overallRating?: number;
  excludeHeadings?: string[];
  rendererProps?: StructuredContentRendererProps;
}) => {
  const inheritedRendererProps = useContext(StructuredContentRendererPropsContext);
  const resolvedRendererProps = rendererProps ?? inheritedRendererProps;

  if (typeof doc === 'string') {
    return <Typography>{doc}</Typography>;
  }

  let normalizedDoc: StructuredContent = populateTableOfContents(doc);
  if (doc && tableOfContentsEnabled) {
    normalizedDoc = groupedByH2ContentAndWrapItWithContainer(doc, excludeHeadings);
  }

  if (overallRating) {
    normalizedDoc = injectRatingIntoStarRatingCategories(normalizedDoc, overallRating);
  }

  return (
    <StructuredContentRendererPropsContext.Provider value={resolvedRendererProps}>
      {[normalizedDoc].flat().map((child, idx) => {
        if (typeof child === 'string')
          return (
            <Typography key={`sc-doc-text-${idx}`} component="span">
              {child}
            </Typography>
          );

        if (!child) {
          return null;
        }

        const Component = components[child.type];
        if (child.type === 'Citation') {
          // Document order. `new Date()` here is a blocking prerender under cacheComponents.
          return (
            <NodeContextProvider path={`citation_${idx}`} key={`citation_${idx}`}>
              <Citation {...(child.props as unknown as ICitation)} />
            </NodeContextProvider>
          );
        }
        if (Component) {
          if (child.type === 'AccordionItem') {
            return <Component {...child} key={idx} structuredContentChildIndex={idx} />;
          }
          if (child.type === 'BankingCalculator' && resolvedRendererProps?.BankingCalculator) {
            return (
              <Component
                {...child}
                key={idx}
                props={{
                  ...(child.props ?? {}),
                  ...resolvedRendererProps.BankingCalculator,
                }}
              />
            );
          }
          if (child.type === 'FAQ' && resolvedRendererProps?.FAQ) {
            return (
              <Component
                {...child}
                key={idx}
                props={{
                  ...(child.props ?? {}),
                  ...resolvedRendererProps.FAQ,
                }}
              />
            );
          }
          if (child.type === 'TableOfContents' && resolvedRendererProps?.TableOfContents) {
            return (
              <Component
                {...child}
                key={idx}
                props={{
                  ...(child.props ?? {}),
                  ...resolvedRendererProps.TableOfContents,
                }}
              />
            );
          }
          return <Component {...child} key={idx} />;
        }
        console.warn(
          `Structured Content Renderer missing component for type ${child.type}`,
          child.props,
        );
        return null;
      })}
    </StructuredContentRendererPropsContext.Provider>
  );
};
